<template>
  <div :class="cn(styles.input.container, props.class)">
    <slot name="prepend">
      <InputItems
        :icon="props.icon"
        :avatar="props.avatar"
        :ui-config="{ input: { items: props.uiConfig?.input?.items } }"
      />
    </slot>

    <slot v-bind="{ modelValue, styles, delegatedProps }">
      <input
        ref="input"
        v-bind="delegatedProps"
        v-model="modelValue"
        :class="styles.input.field"
        :data-testid="`input-${kebabCase(props.id || props.type)}`"
      />
    </slot>

    <slot name="append">
      <InputItems
        :icon="props.iconAppend"
        :avatar="props.avatarAppend"
        :ui-config="{ input: { items: props.uiConfig?.input?.items } }"
      />
    </slot>
  </div>
</template>

<script lang="ts" setup>
// --- external
import IMask, { type InputElement } from "imask";
import { useTemplateRef, computed, onMounted, watch, ref } from "vue";
import type { InputMask } from "imask";
// --- internal
import config from "./input.config";
// --- components
import InputItems from "./InputItems.vue";
import { useStyles, cn } from "../../utils";
import { kebabCase } from "lodash-es";
// --- utils
import { omit } from "lodash-es";
// --- types
import type { InputProps } from "./types";
// -----------------------------------------------------------------------------

const props = withDefaults(defineProps<InputProps>(), {
  width: "full",
  ring: true,
  // ---
  uiConfig: () => ({ input: {} }),
  class: ""
});

const input = useTemplateRef<InputElement>("input");
const modelValue = defineModel<InputProps["modelValue"]>("modelValue", {});

const delegatedProps = computed(
  (): Omit<
    InputProps,
    | "class"
    | "uiConfig"
    | "defaultValue"
    | "modelValue"
    | "width"
    | "size"
    | "icon"
    | "avatar"
    | "iconAppend"
    | "avatarAppend"
    | "autoFocus"
  > =>
    omit(props, [
      "class",
      "uiConfig",
      "defaultValue",
      "modelValue",
      "width",
      "size",
      "icon",
      "avatar",
      "iconAppend",
      "avatarAppend",
      "autoFocus"
    ])
);

const meta = computed(() => ({
  width: props.width,
  hasRing: props.ring
}));

const styles = useStyles(
  ["container", "input"],
  meta,
  config,
  props.uiConfig ?? {}
);

const maskedInstance = ref<InputMask<any> | null>(null);

onMounted(() => {
  applyMask();
});

watch(
  () => props.mask,
  () => {
    if (maskedInstance.value) {
      maskedInstance.value.destroy();
      maskedInstance.value = null;
    }
    applyMask();
  }
);

function applyMask() {
  if (props.mask && input.value) {
    const maskOptions = {
      mask: props.mask as any
    };

    maskedInstance.value = IMask(input.value, maskOptions);

    maskedInstance.value.on("accept", () => {
      modelValue.value = maskedInstance.value?.value;
    });
  }
}
</script>
